import React from "react";
import classes from "./Experience.module.css";
import { CardTimeline, CardTimelineItem } from "src/components/CardTimeline";

type Props = {
  alwaysOpen?: boolean;
};

const Experience = ({ alwaysOpen }: Props) => {
  const experienceItems: CardTimelineItem[] = [
    {
      iconClassName: classes["experience-icon-tegna"],
      title: "Platform Engineering Manager",
      employer: "TEGNA",
      start: "Jun 2026",
      end: "Present",
      content: {
        description:
          "Promoted from Solutions Architect to lead the platform and CMS domains. I manage the front-end internal CMS product teams (React web and Swift mobile) alongside the multi-cloud .NET Microservices and DevOps teams - while still owning architecture and getting hands-on to support consistent velocity, often via Terraform and click-ops.",
        sections: [
          {
            title: "Engineering Leadership",
            content: [
              {
                title: "Team Management",
                description:
                  "Managing the CMS front-end product teams and the multi-cloud Microservices and DevOps teams. Owning workforce (budget, roadmap, and staffing) planning, performance reviews, career development, and hiring across all 7 reporting teams.",
              },
              {
                title: "Architectural Direction",
                description:
                  "Still the architectural voice on the platform: setting technical direction, reviewing designs, analyzing performance and cost efficiency, and dropping into code when needed",
              },
              {
                title: "Cross-Org Partnership",
                description:
                  "Interfacing with product, data, analytics, and operations teams to keep the platform aligned with leadership goals and the day-to-day needs of station operators and content creators.",
              },
            ],
          },
          {
            title: "Platform Health & Reporting",
            content: [
              {
                title: "Operational Analysis",
                description:
                  "Performing regular analysis across the platform to produce reports on operational cost, system stability, and monitoring/visibility coverage; turning them into a prioritized backlog of improvement opportunities.",
              },
            ],
          },
        ],
      },
      skills: [
        {
          category: "Leadership",
          skills: [
            "Team Management",
            "Performance Reviews",
            "Career Development",
            "Hiring",
            "Roadmap Planning",
            "Cross-Team Delivery",
          ],
        },
        {
          category: "Architecture",
          skills: [
            "Multi-Cloud (Azure/AWS)",
            "Distributed Systems",
            "Event-driven design",
            "IaC / Terraform",
          ],
        },
        {
          category: "Domains",
          skills: ["Web CMS", "Mobile CMS", "Microservices", "DevOps"],
        },
      ],
    },
    {
      iconClassName: classes["experience-icon-tegna"],
      title: "Solutions Architect",
      employer: "TEGNA",
      start: "Feb 2026",
      end: "Jun 2026",
      content: {
        description:
          "TEGNA is a media company operating 64 TV stations across 51 US markets. My role sat at the intersection of architecture, delivery leadership, and production ownership for the CMS and platform teams; equal parts design work, cross-team coordination, and firefighting when things went sideways in production.",
        sections: [
          {
            title: "Platform Architecture & Technical Ownership",
            content: [
              {
                title: "AI Cost Optimization",
                description:
                  "Reworked our AI and content-management usage patterns to reduce AI spend by ~90% - roughly $6M annually - without sacrificing output quality.",
              },
              {
                title: "Azure Infrastructure Design",
                description:
                  "Designed and documented infrastructure solutions with a focus on cost modeling, DoS surface reduction, and long-term operational sustainability. That includes partition/indexing strategies, compute autoscaling, APIM policy layers, Traffic Manager failover, and cross-region active-active configurations.",
              },
              {
                title: "Cosmos DB Redesign",
                description:
                  "Redesigned our Cosmos DB storage approaches to heavily cut RU consumption and allow performant, predictable capacity scaling by taking partition and index pressure off of hot paths.",
              },
              {
                title: "CMS React Migration",
                description:
                  "Architected the full rebuild and migration of a legacy JavaScript/ASP.NET CMS into a modern TypeScript/React micro-frontend architecture; defining MFE partition boundaries, Module Federation, shared package contracts, BFF patterns, and Akamai edge delivery, along with the design patterns, coding standards, boilerplates, and reference documentation. Backed it with CI/CD pipelines supporting decoupled releases across feature/team domains.",
              },
              {
                title: "Incident Response",
                description:
                  "Investigated and remediated production incidents end-to-end, delivering RCAs and phased resolution plans within a 24H response window. Exemplary incidents include Traffic Manager silent failures, Cosmos DB partition ceiling breaches, and unfiltered CloudWatch logs hemorrhaging into our monitoring solution at significant cost.",
              },
              {
                title: "Production Release Reviews",
                description:
                  "Conducted release reviews for cross-platform impact and production risks before things ship. I've caught CSRF gaps, unbounded payload sizes, missing autoscale ceilings, and downstream service saturation issues that would've been painful to diagnose post-deployment.",
              },
              {
                title: "Tech Briefs & Backend Patterns",
                description:
                  "Architected backend patterns for new platform capabilities and produced tech briefs that capture decisions, tradeoffs, and infrastructure requirements. Examples include a resource-partitioned priority queue design, AI/ML token optimization strategy, polyglot persistent data-layer redesign, and a vendor-portable SaaS integration layer supporting provider agnosticism.",
              },
            ],
          },
          {
            title: "Delivery Leadership & Engineering Standards",
            content: [
              {
                title: "Process Standards",
                description:
                  "Owned engineering process standards across sprint estimation, story point carry-over policies, branch management strategy, and decision delegation frameworks with explicit escalation tiers, so team leads know the exact line between independent action and escalation.",
              },
              {
                title: "Product Partnership",
                description:
                  "Served as the primary engineering counterpart to CMS product leadership: authoring quarterly tech briefs, sizing efforts, defining initiative scopes, surfacing dependencies, and resolving open questions before timelines hardened into delivery contracts.",
              },
              {
                title: "Observability & Cost Optimization",
                description:
                  "Drove observability strategy across our monitoring layer (New Relic, AWS CloudWatch, and Azure Monitor). This included namespace-level metric filtering, log subscription cleanup, agent coverage gap analysis, and cross-platform philosophy alignment to improve our signal-to-noise ratio.",
              },
            ],
          },
        ],
      },
      skills: [
        {
          category: "Architecture",
          skills: [
            "Akamai CDN/WAF", 
            "Azure & AWS Cloud",
            "CI/CD",
            "Micro-Frontend (MFE)",
            "Microservices",
            "Microsoft Foundry",
          ],
        },
        {
          category: "Observability",
          skills: ["New Relic", "AWS CloudWatch", "Azure Monitor"],
        },
        {
          category: "Development",
          skills: [
            "C#/.NET", 
            "Node.js", 
            "React", 
            "Terraform",
            "TypeScript",
          ],
        },
      ],
    },
    {
      iconClassName: classes["experience-icon-contract"],
      title: "AI/ML Engineer",
      employer: "Consulting",
      start: "Jul 2024",
      end: "Feb 2026",
      content: {
        description:
          "Taking on AI integration projects that solve real problems. Recent work includes building systems that actually use AI where it makes sense - not just because it's trendy.",
        sections: [
          {
            title: "AI-Powered Tutoring Platform",
            description:
              "Built an adaptive learning system that figures out what students struggle with by analyzing their answers. The system uses LLM APIs to generate targeted questions and adjusts difficulty on the fly. The interesting challenge here was making the analysis pipeline fast enough that students don't notice the AI working in the background.",
          },
          {
            title: "Document Digitization System",
            description:
              'Created a pipeline that takes scanned documents, OCRs them, and makes them searchable through natural language queries. Think "Find all mentions of Project Phoenix in Q3" instead of Ctrl+F. Built the whole stack from OCR processing to vector database integration to a chat interface that actually cites its sources.',
          },
        ],
      },
      skills: [
        {
          category: "Development",
          skills: [
            "TypeScript",
            "Node.js",
            "React",
            "React Native",
            "Expo",
            "Python",
            "PostgreSQL",
            "pgvector",
            "Docker",
            "Cosmos DB",
            "Azure DevOps & Cloud Services",
          ],
        },
        {
          category: "Technology",
          skills: [
            "OpenAI",
            "Tesseract",
            "Qdrant",
            "Elasticsearch",
            "Llama Index",
            "Hugging Face",
          ],
        },
      ],
    },
    {
      iconClassName: classes["experience-icon-kapex"],
      title: "Software Engineering Manager & Lead Architect",
      employer: "Kapex Technology",
      start: "Feb 2022",
      end: "June 2024",
      content: {
        description:
          "This was a role where I got to wear both hats - managing three development teams while also being the primary architect and developer for our core systems. The result meant coordinating with teams across multiple time zones while still shipping production code.",
        sections: [
          {
            title: "Systems I've Authored",
            content: [
              {
                title: "Payments Microservice",
                description:
                  "Built the entire payments system from scratch - Stripe integration, idempotent processing (because charging someone twice is bad), multi-tenant architecture, refunds, scheduled payments, ACH, card present, saved payment methods, the works. This was one of the most complex systems I've ever built, and it was a pleasure to see it in action.",
              },
              {
                title: "Custom RBAC System",
                description:
                  "Designed and implemented a role-based access control system for 10k+ users across 20+ organizations. This wasn't just \"admin vs user\" - we needed fine-grained permissions, role inheritance, and resource-level authorization. Turns out building your own auth system is way harder than it looks (which is why most people don't do it).",
              },
              {
                title: "GraphQL Framework Layer",
                description:
                  "This is probably the developer tooling I'm most proud of. Built a framework that sits on top of TypeORM and automatically handles pagination, filtering, and sorting for any data model. Other developers just describe their data model and boom - instant pagination. Reduced boilerplate by about 80% and made code reviews way less painful. It is supported by a front-end table component/query builder but allows for resolver customization as well.",
              },
              {
                title: "React Query Builder Component",
                description:
                  "Created a React component that auto-generates GraphQL queries based on user interactions. Frontend devs can now build complex filterable, sortable tables without ever writing a GraphQL query. They just configure the columns and the component does the rest.",
              },
            ],
          },
          {
            title: "Client Projects Across Industries",
            description:
              'One of the interesting parts of this role was working on custom software for clients in heavily regulated industries. This meant learning to build systems where "it works" isn\'t enough—it has to work correctly, securely, and provably so.',
            content: [
              {
                title: "Fintech Platform",
                description:
                  "Built portfolio management application for financial advisors managing client assets. This required designing systems with financial-grade security, audit trails for every transaction, and compliance with fiduciary regulations. When you're handling people's retirement accounts, you learn real fast that idempotent operations aren't optional.",
              },
              {
                title: "Healthcare Platforms",
                description:
                  "Worked on projects in hospice, mental health services, and other domains where data sensitivity and regulatory compliance weren't nice-to-haves—they were requirements from day one.",
              },
            ],
          },
          {
            title: "Infrastructure Work",
            content: [
              {
                description:
                  "Set up the entire CI/CD pipeline with Azure DevOps for containerized microservices, including setting up a private Docker registry and containerizing all our services. This was a huge win for us as it allowed us to scale our infrastructure more easily and reduce the time it took to deploy new features.",
              },
              {
                description:
                  "Implemented service bus architecture for async communication between services, which allowed for more efficient and scalable communication between our microservices. This was particularly important for our payments system, which required real-time communication between services to ensure that transactions were processed correctly.",
              },
              {
                description:
                  "Cut infrastructure costs by 42% by analyzing our usage vs. our allocated resources. This involved identifying inefficiencies in our infrastructure and implementing solutions to optimize resource usage, such as scaling down underutilized services and implementing auto-scaling for critical services.",
              },
              {
                description:
                  "Designed the multi-tenant SQL Server schema, which allowed us to support multiple organizations and users within a single database instance. This was a significant challenge, but it was also a great opportunity to learn more about multi-tenant architecture and how to design systems that can scale effectively.",
              },
            ],
          },
        ],
      },
      skills: [
        {
          category: "Development",
          skills: [
            "TypeScript",
            "Node.js",
            "GraphQL",
            "Apollo",
            "TypeORM",
            "React",
            "SQL Server",
            "Azure DevOps & Cloud Services",
            "Docker",
            "React Native",
            "SaaS Integrations",
          ],
        },
        {
          category: "SaaS Integrations",
          skills: [
            "Stripe",
            "Payroc",
            "DocuSign",
            "Plaid",
            "Yahoo Finance",
            "Twilio",
            "OpenID Connect",
          ],
        },
      ],
    },
    {
      iconClassName: classes["experience-icon-truecare"],
      title: "Software Engineer",
      employer: "TrueCare",
      start: "Jul 2020",
      end: "Feb 2022",
      content: {
        description:
          "This was my full-stack grind phase - building and maintaining production apps across web, iOS, and Android for health and insurance clients. Here I learned a ton about maintaining real user-facing applications and the consequences of bad architecture decisions. TrueCare's core business was medical records management, which meant everything I built had to comply with HIPAA, FHIR, and HL7 standards. \n Working in healthcare tech teaches you that security isn't a feature you add later—it's baked into every architectural decision. You learn to design systems where data access is traceable, auditable, and locked down by default. And you learn that \"it works on my machine\" doesn't cut it when you're dealing with protected health information.",
        sections: [
          {
            title: "General Development Work",
            content: [
              {
                description:
                  "Built and maintained 5+ production applications - mix of React web apps and React Native mobile apps all handling sensitive health data. This was a great learning experience for me, as it taught me how to build and maintain large-scale applications that handle sensitive data.",
              },
              {
                description:
                  "Backend work with Node.js and .NET microservices backed by PostgreSQL and SQL Server",
              },
              {
                description:
                  "Led the migration from JavaScript to TypeScript - this was tedious but absolutely worth it for the improved code transparency. Additionally, catching type errors at compile time instead of production is crucial for industries that require 24/7 uptime.",
              },
              {
                description:
                  "Built a GraphQL API layer that consolidated multiple REST services while maintaining HIPAA compliance and audit logging",
              },
            ],
          },
        ],
      },
      skills: [
        {
          category: "Developement",
          skills: [
            "React",
            "React Native",
            "TypeScript",
            "Node.js",
            "GraphQL",
            ".NET/C#",
            "PostgreSQL",
            "SQL Server",
          ],
        },
        {
          category: "Compliance",
          skills: ["HIPAA", "FHIR", "HL7"],
        },
      ],
    },
  ];

  return (
    <div className={classes["Experience-wrapper"]}>
      <CardTimeline itemList={experienceItems} alwaysOpen={alwaysOpen} />
    </div>
  );
};

export default Experience;
